// Utilities for the Molds list page: status computation, PM due, risk reasons, sorting
import type { Mold, WorkOrder, PMPlan, MoldLocation, HealthStatus } from '@/types/maintenance';
import type { ComputedMoldStatus } from '@/components/mold/MoldComputedStatus';
import { computeMoldStatus } from '@/components/mold/MoldComputedStatus';
import { failureCodeDescriptions } from '@/data/codeDescriptions';

// ============ LOCATION LABELS ============

export const locationLabels: Record<MoldLocation, string> = {
  toolroom: 'Toolroom',
  line: 'Production Line',
  storage: 'Storage',
};

export function getLocationDisplay(mold: Mold): string {
  const base = locationLabels[mold.location];
  if ((mold.location === 'storage' || mold.location === 'toolroom') && mold.storage_location_code) {
    return `${base} (${mold.storage_location_code})`;
  }
  return base;
}

// ============ BLOCKED REASON ============

export type BlockedReasonLabel = 'Maintenance' | 'Hold' | 'Line Down WO' | 'Safety WO' | 'Quality WO';

export function getBlockedReason(mold: Mold, openWOs: WorkOrder[]): BlockedReasonLabel | null {
  const hasLineDown = openWOs.some(wo => wo.impact === 'line_down');
  if (hasLineDown) return 'Line Down WO';
  const hasSafety = openWOs.some(wo => wo.impact === 'safety');
  if (hasSafety) return 'Safety WO';
  const hasQuality = openWOs.some(wo => wo.impact === 'quality');
  if (hasQuality) return 'Quality WO';
  if (mold.status === 'on_hold') return 'Hold';
  if (mold.status === 'in_maintenance') return 'Maintenance';
  return null;
}

// ============ PM DUE STATUS ============

export function computePMDueStatus(mold: Mold, pmPlan?: PMPlan): HealthStatus {
  if (!pmPlan || pmPlan.next_due_shots == null) return 'green';
  const current = mold.current_shots;
  if (current == null) return 'green';
  const delta = pmPlan.next_due_shots - current;
  if (delta <= 0) return 'red';
  if (delta < 5000) return 'amber';
  return 'green';
}

// ============ PM DUE ============

export interface PMDueInfo {
  label: string;        // e.g. "Overdue by 10,000" or "Due in 45,000" or "PM not set"
  delta: number | null; // negative = overdue, positive = due in, null = not set
  currentShots: number | null;
  isOverdue: boolean;
}

export function computePMDue(mold: Mold, pmPlan?: PMPlan): PMDueInfo {
  if (!pmPlan || !pmPlan.shot_interval) {
    return { label: 'PM not set', delta: null, currentShots: mold.current_shots ?? null, isOverdue: false };
  }

  const nextDue = pmPlan.next_due_shots;
  const current = mold.current_shots;

  if (nextDue == null || current == null) {
    return { label: 'PM not set', delta: null, currentShots: current ?? null, isOverdue: false };
  }

  const delta = nextDue - current;
  if (delta < 0) {
    return {
      label: `Overdue by ${Math.abs(delta).toLocaleString()}`,
      delta,
      currentShots: current,
      isOverdue: true,
    };
  }
  return {
    label: `Due in ${delta.toLocaleString()}`,
    delta,
    currentShots: current,
    isOverdue: false,
  };
}

// ============ RISK REASONS ============

export interface RiskReason {
  text: string;
  type: 'pm_overdue' | 'pm_due_soon' | 'repeat_failure' | 'blocked_wo' | 'temp_fix' | 'chronic';
  link?: string; // route to navigate to
}

export function computeRiskReasons(
  mold: Mold,
  openWOs: WorkOrder[],
  allWOs: WorkOrder[],
  pmDue: PMDueInfo,
): RiskReason[] {
  const reasons: RiskReason[] = [];

  // PM overdue
  if (pmDue.isOverdue && pmDue.delta != null) {
    reasons.push({
      text: `PM overdue by ${Math.abs(pmDue.delta).toLocaleString()} shots`,
      type: 'pm_overdue',
      link: `/maintenance/molds/${mold.id}`,
    });
  } else if (pmDue.delta != null && pmDue.delta > 0 && pmDue.delta < 5000) {
    reasons.push({
      text: `PM due soon (${pmDue.delta.toLocaleString()} shots remaining)`,
      type: 'pm_due_soon',
      link: `/maintenance/molds/${mold.id}`,
    });
  }

  // Repeat failure (same failure code ≥2 times in last 90 days)
  const now = new Date();
  const ninetyDaysAgo = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
  const recentDone = allWOs.filter(
    wo => wo.mold_id === mold.id && wo.status === 'closed' && wo.type === 'corrective' && wo.failure_code && wo.end_time
  );
  const recentFailures = recentDone.filter(wo => new Date(wo.end_time!) >= ninetyDaysAgo);
  const failureCounts: Record<string, number> = {};
  recentFailures.forEach(wo => {
    if (wo.failure_code) failureCounts[wo.failure_code] = (failureCounts[wo.failure_code] || 0) + 1;
  });
  const repeatCode = Object.entries(failureCounts).find(([, count]) => count >= 2);
  if (repeatCode) {
    const desc = failureCodeDescriptions[repeatCode[0]];
    const label = desc ? desc.label : repeatCode[0];
    reasons.push({
      text: `Repeat failure: ${label}`,
      type: 'repeat_failure',
      link: `/maintenance/molds/${mold.id}`,
    });
  }

  // Open blocked WO
  const blockedWOs = openWOs.filter(wo => wo.status === 'blocked');
  blockedWOs.forEach(wo => {
    const reasonMap: Record<string, string> = {
      parts: 'Parts',
      qa_hold: 'QA Hold',
      supplier: 'Supplier',
      waiting_approval: 'Waiting Approval',
      no_manpower: 'No Manpower',
    };
    const reason = wo.blocked_reason ? reasonMap[wo.blocked_reason] || wo.blocked_reason : 'Blocked';
    reasons.push({
      text: `Open WO blocked: ${reason}`,
      type: 'blocked_wo',
      link: `/maintenance/work-orders/${wo.id}`,
    });
  });

  // Last fix was temporary
  const lastCorrectiveDone = allWOs
    .filter(wo => wo.mold_id === mold.id && wo.status === 'closed' && wo.type === 'corrective' && wo.end_time)
    .sort((a, b) => new Date(b.end_time!).getTime() - new Date(a.end_time!).getTime());
  if (lastCorrectiveDone.length > 0 && lastCorrectiveDone[0].fix_type === 'temporary') {
    reasons.push({
      text: 'Last fix was temporary',
      type: 'temp_fix',
      link: `/maintenance/work-orders/${lastCorrectiveDone[0].id}`,
    });
  }

  // Chronic flag
  if (mold.chronic_flag) {
    reasons.push({
      text: 'Chronic failure pattern',
      type: 'chronic',
      link: `/maintenance/molds/${mold.id}`,
    });
  }

  return reasons;
}

// ============ ENRICHED MOLD ROW ============

export interface MoldListRow {
  mold: Mold;
  status: ComputedMoldStatus;
  blockedReason: BlockedReasonLabel | null;
  pmDue: PMDueInfo;
  riskReasons: RiskReason[];
  hasRisk: boolean;
  sortPriority: number; // lower = higher priority
}

export function buildMoldListRows(
  molds: Mold[],
  getWOsByMold: (moldId: string) => WorkOrder[],
  allWOs: WorkOrder[],
  pmPlans: PMPlan[],
): MoldListRow[] {
  return molds.map(mold => {
    const moldWOs = getWOsByMold(mold.id);
    const openWOs = moldWOs.filter(wo => wo.status !== 'closed');
    const status = computeMoldStatus(mold, openWOs);
    const blockedReason = (status === 'blocked' || status === 'on_hold') ? getBlockedReason(mold, openWOs) : null;
    const pmPlan = pmPlans.find(p => p.mold_id === mold.id);
    const pmDue = computePMDue(mold, pmPlan);
    const riskReasons = computeRiskReasons(mold, openWOs, allWOs, pmDue);
    // Don't show risk icon for blocked/on_hold molds
    const hasRisk = status !== 'blocked' && status !== 'on_hold' && riskReasons.length > 0;

    // Sorting: blocked/on_hold first, then in_maintenance, then at-risk, rest
    let sortPriority: number;
    if (status === 'blocked' || status === 'on_hold') sortPriority = 0;
    else if (status === 'in_maintenance') sortPriority = 1;
    else if (status === 'in_production' && riskReasons.length > 0) sortPriority = 2;
    else if (status === 'available' && riskReasons.length > 0) sortPriority = 3;
    else if (status === 'in_production') sortPriority = 4;
    else sortPriority = 5;

    return { mold, status, blockedReason, pmDue, riskReasons, hasRisk, sortPriority };
  });
}

export function sortMoldRows(rows: MoldListRow[]): MoldListRow[] {
  return [...rows].sort((a, b) => {
    if (a.sortPriority !== b.sortPriority) return a.sortPriority - b.sortPriority;
    // Within same group, sort by PM urgency (most overdue first, then due soonest)
    const aDelta = a.pmDue.delta ?? Infinity;
    const bDelta = b.pmDue.delta ?? Infinity;
    return aDelta - bDelta;
  });
}
